// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Posegraf editor's rig (frontend-aev.5, .6): blocks as bones, splits as
 * joints, the selection rules and the block suggestions. Pure and DOM-free —
 * `RigScene` draws what this computes, `GraphViewPage` keeps the state.
 *
 * A selection is a set of block KEYS, not ids: ruxd renumbers block ids
 * 1..n on every change, but a block's first frame only changes when that
 * block itself is split or merged — so a selection keyed on `first_node`
 * survives a reload (frontend-aev.6 acceptance).
 */

import type { BlockSplit, BlockSplitReason, BlockSuggestions, PoseBlock, PoseBlockInput, PoseGraph } from '../api/types';
import { poseTranslation } from '../data/framesModel';

export type Vec3 = [number, number, number];

/** A block's stable key: its first frame. */
export type BlockKey = number;
export type RigSelection = ReadonlySet<BlockKey>;

export const EMPTY_SELECTION: RigSelection = new Set<number>();

/** One bone: a block and the posed cameras along it, in node order. */
export interface RigBone {
  block: PoseBlock;
  key: BlockKey;
  nodes: number[];
  positions: Vec3[];
  centroid: Vec3 | null;
}

/** A joint: where one bone of a session ends and the next begins. */
export interface RigJoint {
  /** First frame of the following block. */
  node: number;
  prevKey: BlockKey;
  nextKey: BlockKey;
  session: number;
  /** The following block is detached (no odometry across the joint). */
  detached: boolean;
  position: Vec3 | null;
}

export interface Rig {
  bones: RigBone[];
  joints: RigJoint[];
  /** Posed node id → its bone's key. */
  boneOf: Map<number, BlockKey>;
}

export const EMPTY_RIG: Rig = { bones: [], joints: [], boneOf: new Map() };

/** World position of every posed node. */
export function nodePositions(graph: PoseGraph | null): Map<number, Vec3> {
  const out = new Map<number, Vec3>();
  for (const n of graph?.nodes ?? []) {
    const t = poseTranslation(n.pose);
    if (t) out.set(n.id, t);
  }
  return out;
}

export function centroidOf(points: readonly Vec3[]): Vec3 | null {
  if (points.length === 0) return null;
  let x = 0;
  let y = 0;
  let z = 0;
  for (const p of points) {
    x += p[0];
    y += p[1];
    z += p[2];
  }
  return [x / points.length, y / points.length, z / points.length];
}

/** Blocks in node order (the server's order, made sure). */
export function sortedBlocks(blocks: readonly PoseBlock[]): PoseBlock[] {
  return [...blocks].sort((a, b) => a.first_node - b.first_node);
}

/** Bones and joints from the graph and the block list. */
export function buildRig(graph: PoseGraph | null, blocks: readonly PoseBlock[]): Rig {
  const pos = nodePositions(graph);
  const ids = [...pos.keys()].sort((a, b) => a - b);
  const ordered = sortedBlocks(blocks);
  const bones: RigBone[] = [];
  const boneOf = new Map<number, BlockKey>();
  for (const block of ordered) {
    const nodes = ids.filter((id) => id >= block.first_node && id <= block.last_node);
    const positions = nodes.map((id) => pos.get(id) as Vec3);
    for (const id of nodes) boneOf.set(id, block.first_node);
    bones.push({ block, key: block.first_node, nodes, positions, centroid: centroidOf(positions) });
  }
  const joints: RigJoint[] = [];
  for (let i = 1; i < bones.length; i += 1) {
    const prev = bones[i - 1].block;
    const next = bones[i].block;
    if (prev.session !== next.session) continue;
    joints.push({
      node: next.first_node,
      prevKey: prev.first_node,
      nextKey: next.first_node,
      session: next.session,
      detached: next.detached,
      position: bones[i].positions[0] ?? null,
    });
  }
  return { bones, joints, boneOf };
}

// ------------------------------------------------------------- selection --

export interface Modifiers {
  shift?: boolean;
  ctrl?: boolean;
}

/** Click a bone: select it alone; shift adds it (or takes it out again). */
export function clickBone(selection: RigSelection, key: BlockKey, mods: Modifiers = {}): RigSelection {
  if (!mods.shift) return new Set([key]);
  const next = new Set(selection);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** The chain from block `key` to the end of its session (FK tail). */
export function chainFrom(blocks: readonly PoseBlock[], key: BlockKey): BlockKey[] {
  const start = blocks.find((b) => b.first_node === key);
  if (!start) return [];
  return sortedBlocks(blocks)
    .filter((b) => b.session === start.session && b.first_node >= start.first_node)
    .map((b) => b.first_node);
}

/**
 * Click a joint: ctrl selects the chain from the joint to the end of the
 * session (shift adds it to the selection); a plain click selects the bone
 * that starts there.
 */
export function clickJoint(
  selection: RigSelection,
  blocks: readonly PoseBlock[],
  joint: Pick<RigJoint, 'nextKey'>,
  mods: Modifiers = {},
): RigSelection {
  if (!mods.ctrl) return clickBone(selection, joint.nextKey, mods);
  const chain = chainFrom(blocks, joint.nextKey);
  const next = new Set(mods.shift ? selection : []);
  for (const k of chain) next.add(k);
  return next;
}

export interface ScreenRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * Box-select on the plan: every bone with at least one camera inside the
 * rectangle. `projected` holds each bone's cameras as screen x, y pairs
 * (NaN = off screen). Shift adds to the selection.
 */
export function boxSelect(
  selection: RigSelection,
  projected: ReadonlyMap<BlockKey, ArrayLike<number>>,
  rect: ScreenRect,
  mods: Modifiers = {},
): RigSelection {
  const xmin = Math.min(rect.x0, rect.x1);
  const xmax = Math.max(rect.x0, rect.x1);
  const ymin = Math.min(rect.y0, rect.y1);
  const ymax = Math.max(rect.y0, rect.y1);
  const next = new Set(mods.shift ? selection : []);
  for (const [key, xy] of projected) {
    for (let i = 0; i + 1 < xy.length; i += 2) {
      const x = xy[i];
      const y = xy[i + 1];
      if (x >= xmin && x <= xmax && y >= ymin && y <= ymax) {
        next.add(key);
        break;
      }
    }
  }
  return next;
}

/** Drop keys whose block is gone (split / merged away); same set when nothing changed. */
export function pruneSelection(selection: RigSelection, blocks: readonly PoseBlock[]): RigSelection {
  const keys = new Set(blocks.map((b) => b.first_node));
  const kept = [...selection].filter((k) => keys.has(k));
  return kept.length === selection.size ? selection : new Set(kept);
}

/** The selected blocks, in node order. */
export function selectedBlocks(blocks: readonly PoseBlock[], selection: RigSelection): PoseBlock[] {
  return sortedBlocks(blocks).filter((b) => selection.has(b.first_node));
}

/** Two selected bones that can merge: neighbours in one session (the second into the first). */
export function mergePair(blocks: readonly PoseBlock[], selection: RigSelection): [PoseBlock, PoseBlock] | null {
  const sel = selectedBlocks(blocks, selection);
  if (sel.length !== 2) return null;
  const ordered = sortedBlocks(blocks);
  const i = ordered.indexOf(sel[0]);
  const [a, b] = sel;
  if (ordered[i + 1] !== b || a.session !== b.session) return null;
  return [a, b];
}

/** Centroid of the selection's cameras (the default gizmo pivot). */
export function selectionCentroid(rig: Rig, selection: RigSelection): Vec3 | null {
  const pts: Vec3[] = [];
  for (const bone of rig.bones) if (selection.has(bone.key)) pts.push(...bone.positions);
  return centroidOf(pts);
}

/** The joint at the start of the first selected bone (the "joint" pivot), if it has one. */
export function selectionJoint(rig: Rig, selection: RigSelection): RigJoint | null {
  const first = rig.bones.find((b) => selection.has(b.key));
  if (!first) return null;
  return rig.joints.find((j) => j.nextKey === first.key) ?? null;
}

// ----------------------------------------------------------- suggestions --

export const REASON_LABEL: Record<BlockSplitReason, string> = {
  session: 'Ny session',
  time_gap: 'Tidsspring',
  tracking_gap: 'Tracking tabt',
  storey_change: 'Etageskift',
  odometry_jump: 'Odometrispring',
};

/** Danish one-liner for a suggestion's tooltip and list row. */
export function suggestionText(split: BlockSplit): string {
  const fmt = (v: number, d = 1) => v.toLocaleString('da-DK', { maximumFractionDigits: d, minimumFractionDigits: d });
  switch (split.reason) {
    case 'session':
      return 'En ny session begynder';
    case 'time_gap':
      return `${fmt(split.gap_s, 0)} s uden billeder`;
    case 'tracking_gap':
      return `Tracking tabt i ${fmt(split.gap_s)} s`;
    case 'storey_change':
      return `Kameraet skifter etage (${fmt(split.jump_m, 2)} m spring)`;
    case 'odometry_jump':
      return `Odometrien springer ${fmt(split.jump_m, 2)} m`;
    default:
      return split.detail;
  }
}

/** The suggestions still to decide: not already a block start, not ignored; strongest first. */
export function openSuggestions(
  suggestions: BlockSuggestions | null,
  blocks: readonly PoseBlock[],
  ignored: ReadonlySet<number>,
): BlockSplit[] {
  if (!suggestions) return [];
  const starts = new Set(blocks.map((b) => b.first_node));
  return suggestions.splits
    .filter((s) => !starts.has(s.node_id) && !ignored.has(s.node_id))
    .sort((a, b) => b.score - a.score || a.node_id - b.node_id);
}

/**
 * The block list with extra starts at `splitNodes` (accept all): each split
 * cuts the block holding it; frames before the split stay in the old block,
 * which keeps its name. `posed` is every posed node id (the graph's). The
 * new blocks go unnamed, so the server names them `s<k>.<j>`.
 */
export function blocksWithSplits(
  blocks: readonly PoseBlock[],
  splitNodes: readonly number[],
  posed: readonly number[],
): PoseBlockInput[] {
  const ids = [...posed].sort((a, b) => a - b);
  const out: PoseBlockInput[] = [];
  for (const b of sortedBlocks(blocks)) {
    const inside = ids.filter((id) => id >= b.first_node && id <= b.last_node);
    const cuts = [...new Set(splitNodes)].filter((n) => n > b.first_node && n <= b.last_node && inside.includes(n)).sort((x, y) => x - y);
    let first = b.first_node;
    let name: string | undefined = b.name;
    for (const cut of cuts) {
      const before = inside.filter((id) => id < cut);
      out.push({ name, first_node: first, last_node: before[before.length - 1], scan_id: b.scan_id });
      first = cut;
      name = undefined;
    }
    out.push(name === undefined ? { first_node: first, last_node: b.last_node, scan_id: b.scan_id } : { name, first_node: first, last_node: b.last_node, scan_id: b.scan_id });
  }
  return out;
}

/** Sessions with their blocks, for the block panel. */
export function sessionsOf(blocks: readonly PoseBlock[]): { session: number; scanId: number; blocks: PoseBlock[]; frames: number }[] {
  const by = new Map<number, PoseBlock[]>();
  for (const b of sortedBlocks(blocks)) {
    const list = by.get(b.session) ?? [];
    list.push(b);
    by.set(b.session, list);
  }
  return [...by.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([session, list]) => ({
      session,
      scanId: list[0].scan_id,
      blocks: list,
      frames: list.reduce((n, b) => n + b.frame_count, 0),
    }));
}

/** The session's colour token (colourblind-safe categorical scale). */
export function sessionColorToken(session: number): string {
  return `--label-${((session % 8) + 8) % 8}`;
}
